import React from 'react';
import { connect } from 'react-redux';
import { Switch, Route, NavLink } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';

import CommonBackButton from 'containers/CommonBackButton';

import EmailConfiguration from 'components/Settings/EmailConfiguration';
import HomeSettings from 'components/Settings/HomeSettings';
import Master from 'components/Settings/Master';
import ProjectSettings from 'components/Settings/ProjectSettings';
import PromotionalSettings from 'components/Settings/PromotionalSettings';
import QuoteSettings from 'components/Settings/QuoteSettings';
import RolePermission from 'components/Settings/RolePermission';
import SalespersonHandover from 'components/Settings/SalespersonHandover';
import SalespersonHandoverHistory from 'components/Settings/SalespersonHandoverHistory';
import SubMaster from 'components/Settings/SubMaster';
import TermsOfUse from 'components/TermsOfUse/TermsOfUse';

import { Storage } from 'services/storage';
import { handleLogout } from 'services/util';

import { useCompositeState } from 'util/hooks';
import { classNames, isEmpty, isNotEmpty } from 'util/utils';

import { USERS_TYPE } from 'constants/Common';
import { PREFIX_URL_WITH_SLASH as PREFIX_URL } from 'constants/CommonUrl';
import { getPermittedModuleList, MAINMODULE, MODULES } from 'constants/permission';

import ChangePassword from './ChangePassword';

import Configuration from '../Configuration';
import ContactDirectory from '../ContactDirectory';
import EditProfile from '../EditProfile';
import MenuAccess from '../MenuAccess';
import UploadDiamonds from '../UploadDiamonds';
import BidValidator from 'routes/BidValidator';

export const SettingsRoutes = {
  root: `${PREFIX_URL}/setting/master`,
  configuration: `${PREFIX_URL}/setting/configuration`,
  profile: `${PREFIX_URL}/setting/profile`,
  uploaddiamonds: `${PREFIX_URL}/setting/uploaddiamonds`,
  contactDirectory: `${PREFIX_URL}/setting/contact-directory`,
  changePassword: `${PREFIX_URL}/setting/change-password`,
  permission: `${PREFIX_URL}/setting/permission`,
  homeSetting: `${PREFIX_URL}/setting/home-setting`,
  emailConfiguation: `${PREFIX_URL}/setting/email-configuration`,
  projectSetting: `${PREFIX_URL}/setting/project-setting`,
  promotionalSetting: `${PREFIX_URL}/setting/promotional-setting`,
  quoteSetting: `${PREFIX_URL}/setting/quote-setting`,
  submaster: `${PREFIX_URL}/setting/submaster`,
  rolepermission: `${PREFIX_URL}/setting/rolepermission`,
  master: `${PREFIX_URL}/setting/master`,
  terms: `${PREFIX_URL}/setting/terms-and-conditions`,
  bidValidator: `${PREFIX_URL}/setting/bid-validator`,
  salespersonHandover: `${PREFIX_URL}/setting/salesperson-handover`,
  // Intentionally has no nav link -- reachable only by typing the route.
  salespersonHandoverHistory: `${PREFIX_URL}/setting/salesperson-handover-history`,
};

function Settings({ location }) {
  const [user, setUser] = useCompositeState(Storage.get('user'));
  const path = React.useMemo(() => location.pathname, [location.pathname]);

  const userEventCallback = React.useCallback(
    (event) => {
      const user = event?.detail?.user;
      if (isEmpty(user)) return;
      setUser(user);
    },
    [setUser],
  );

  React.useEffect(() => {
    window.addEventListener('storage', userEventCallback);
    return () => {
      window.removeEventListener('storage', userEventCallback);
    };
  }, [userEventCallback]);

  const permission = getPermittedModuleList(MAINMODULE.SETTING, MODULES.SETTING.MY_SETTING.NAME);

  return (
    <div className="settingMainWrapper documentFileWrapper fullPageOpen">
      <ScrollArea className="settingLeftScroll" contentClassName="content" smoothScrolling>
        <div className="settingLeftBlock">
          <CommonBackButton link={`${PREFIX_URL}/dashboard`} />
          <h2 className="seetingMainTitle">Settings</h2>
          <ul>
            <li>
              {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.MASTER) && (
                <NavLink to={SettingsRoutes.master}>Master</NavLink>
              )}
              {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.SUB_MASTER) && (
                <NavLink to={SettingsRoutes.submaster}>Sub Master</NavLink>
              )}
              {/* {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.CAREER) && (
                <NavLink to={SettingsRoutes.career}>Career Master</NavLink>
              )}
              {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.CAREER_APPLICATIONS) && (
                <NavLink to={SettingsRoutes.careerApplications}>Applications</NavLink>
              )}
              {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.FEEDBACK) && (
                <NavLink to={SettingsRoutes.feedback}>Feedback</NavLink>
              )} */}
              {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.ROLE_PERMISSION) && (
                <NavLink to={SettingsRoutes.rolepermission}>Role Permission</NavLink>
              )}
            </li>
          </ul>
          <div className="settingSpereter"></div>
          <ul>
            <h3 className="settingSubTitle">{[user?.firstName, user?.lastName].filter(isNotEmpty).join(' ')}</h3>
            {/* {getPermittedModuleList(MAINMODULE.SETTING, MODULES.SETTING.MY_SETTING.NAME).length > 0 && (
              <li>
                <NavLink to={SettingsRoutes.profile}>My Settings</NavLink>
              </li>
            )} */}
            <li>
              <NavLink to={SettingsRoutes.changePassword}>Change Password</NavLink>
            </li>
            {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.CONTACT_DIRECTORY) && (
              <li>
                <NavLink to={SettingsRoutes.contactDirectory}>Contact Directory</NavLink>
              </li>
            )}
            <li>
              <NavLink to={SettingsRoutes.bidValidator}>Bid Validator</NavLink>
            </li>
            {user?.type === USERS_TYPE.SUPER_ADMIN && (
              <li>
                <NavLink to={SettingsRoutes.salespersonHandover}>Salesperson Handover</NavLink>
              </li>
            )}
            {/* <li>
              <NavLink to={SettingsRoutes.notifications}>Notification</NavLink>
            </li> */}
            {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.HOME_SETTING) && (
              <li>
                <NavLink to={SettingsRoutes.homeSetting}>Home Settings</NavLink>
              </li>
            )}
            {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.PROJECT_SETTING) && (
              <li>
                <NavLink to={SettingsRoutes.projectSetting}>Project Settings</NavLink>
              </li>
            )}
            {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.PROMOTIONAL_SETTING) && (
              <li>
                <NavLink to={SettingsRoutes.promotionalSetting}>Promotinal Settings</NavLink>
              </li>
            )}
            {permission.includes(MODULES.SETTING.MY_SETTING.MODULE.QUOTE_SETTING) && (
              <li>
                <NavLink to={SettingsRoutes.quoteSetting}>Quote Settings</NavLink>
              </li>
            )}
            {/* {user?.type === 1 &&
              <li>
                <NavLink to={SettingsRoutes.uploaddiamonds}>Upload Diamonds</NavLink>
              </li>
            } */}
            <li>
              <a onClick={() => handleLogout()}>Log out</a>
            </li>
          </ul>
          {/* <div className="settingSpereter"></div>
           <ul>
            <h3 className="settingSubTitle">My Apps</h3>
            <li>
              <NavLink to={SettingsRoutes.permission}>Menu Permission</NavLink>
            </li>
            <li>
              <a to="/">Slack Notifications</a>
            </li>
            <li>
              <a to="/">Apps</a>
            </li>
          </ul> */}
        </div>
      </ScrollArea>
      <div className="settingRightBlock">
        <div
          className={classNames([
            `settingRightInneer`,
            [SettingsRoutes.master, SettingsRoutes.submaster, SettingsRoutes.rolepermission].includes(path) && 'p-0',
          ])}
        >
          <Switch>
            <Route path={SettingsRoutes.configuration} component={Configuration} />
            {/* <Route path={SettingsRoutes.notifications} component={Notifications} /> */}
            <Route path={SettingsRoutes.profile} component={EditProfile} />
            <Route path={SettingsRoutes.permission} component={MenuAccess} />
            <Route path={SettingsRoutes.contactDirectory} component={ContactDirectory} />
            <Route path={SettingsRoutes.changePassword} component={ChangePassword} />
            <Route path={SettingsRoutes.homeSetting} component={HomeSettings} />
            <Route path={SettingsRoutes.emailConfiguation} component={EmailConfiguration} />
            <Route path={SettingsRoutes.projectSetting} component={ProjectSettings} />
            <Route path={SettingsRoutes.promotionalSetting} component={PromotionalSettings} />
            <Route path={SettingsRoutes.quoteSetting} component={QuoteSettings} />
            <Route path={SettingsRoutes.master} component={Master} />
            <Route path={SettingsRoutes.submaster} component={SubMaster} />
            {/* <Route path={SettingsRoutes.career} component={Career} /> */}
            {/* <Route path={SettingsRoutes.careerApplications} component={CareerApplications} /> */}
            {/* <Route path={SettingsRoutes.feedback} component={Feedback} /> */}
            <Route path={SettingsRoutes.rolepermission} component={RolePermission} />
            <Route path={SettingsRoutes.uploaddiamonds} component={UploadDiamonds} />
            <Route path={SettingsRoutes.terms} component={TermsOfUse} />
            <Route path={SettingsRoutes.bidValidator} component={BidValidator} />
            {/* Declared before the handover route so it can never be prefix-matched by it.
                There is no nav link to this one on purpose -- see the component's comment. */}
            {user?.type === USERS_TYPE.SUPER_ADMIN && (
              <Route path={SettingsRoutes.salespersonHandoverHistory} component={SalespersonHandoverHistory} />
            )}
            {user?.type === USERS_TYPE.SUPER_ADMIN && (
              <Route path={SettingsRoutes.salespersonHandover} component={SalespersonHandover} />
            )}
          </Switch>
        </div>
      </div>
    </div>
  );
}

export default connect(({ auth }) => ({
  user: auth?.authUser,
}))(Settings);
